import { Box } from "@mui/material";
import CustomLoader from "app/components/common/CustomLoader";
import { getStudentsListLogic } from "app/services/student-management";
import React, { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  selectActiveSession,
  selectSelectedSchool,
} from "app/modules/schools/selectors";
import CertificateHeader from "./components/CertificateHeader";
import StudentTable from "./components/StudentTable";
import PrintCertificateDialog from "./components/PrintCertificateDialog";

const CertificatePrint = () => {
  const selectedSession = useSelector(selectActiveSession);
  const schoolDetail = useSelector(selectSelectedSchool);

  const [selectedClasses, setSelectedClasses] = useState([]);
  const [resData, setResData] = useState([]);
  const [allCheck, setAllCheck] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [emptyState, setEmptyState] = useState(false); // Used to trigger re-renders
  const [classListToMutate, setClassListToMutate] = useState([]);
  const [appliedClassList, setAppliedClassList] = useState([]);
  const [showPrintModal, setShowPrintModal] = useState(false);

  const handleSetData = (res) => {
    const arr = [];
    if (res && res?.length > 0) {
      res?.forEach((item) => {
        arr.push({
          ...item,
          checked: false,
        });
      });
    }
    setResData(arr);
  };

  // Get selected students for printing
  const getSelectedStudents = () => {
    return resData.filter((student) => student.checked);
  };

  // Handle search action
  const handleSearch = () => {
    const sanitized = (classListToMutate || []).map(
      ({ classDocId, section }) => {
        const obj = { classDocId };
        if (section && section !== "ALL") obj.section = section;
        return obj;
      }
    );
    setAppliedClassList(sanitized);
  };

  // Handle print action
  const handlePrint = () => {
    const selectedCount = getSelectedStudents().length;
    if (selectedCount === 0) {
      toast.warning("Please select at least one student to print certificates");
      return;
    }
    setShowPrintModal(true);
  };

  // Build class list from selected filter to pass as query param
  useEffect(() => {
    const filteredList = selectedClasses?.map(({ classDocId, section }) => ({
      classDocId,
      section,
    }));
    setClassListToMutate(filteredList);

    return () => {
      setClassListToMutate([]);
    };
  }, [selectedClasses]);

  // Fetch students using useQuery
  const { 
    isLoading: studentListLoading, 
    isFetching: studentListFetching,
  } = useQuery({
      queryKey: [
        "getCertificateStudents",
        selectedSession,
        JSON.stringify(appliedClassList ?? []),
      ],
      queryFn: () => {
        let body = {};
        if (appliedClassList && appliedClassList.length > 0) {
          body = {
            classList: JSON.stringify(appliedClassList),
            filter: "By Class",
          };
        }
        return getStudentsListLogic({
          ...body,
          session: selectedSession,
        });
      },
      onSuccess: (data) => {
        if (data?.data?.list?.length === 0) {
          setResData([]);
        } else {
          handleSetData(data?.data?.list);
        }
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to fetch students");
      },
      enabled: Boolean(selectedSession),
      keepPreviousData: true,
    });

  // Reset checked state when class filter changes
  useEffect(() => {
    if (!classListToMutate || classListToMutate.length === 0) {
      setResData((prev) => prev.map((x) => ({ ...x, checked: false })));
      setAllCheck(false);
    }
  }, [classListToMutate]);

  // Handle select all checkbox
  useEffect(() => {
    if (allCheck) {
      resData.forEach((item, ind) => {
        resData[ind]["checked"] = true;
      });
      setEmptyState((prev) => !prev);
    } else {
      resData.forEach((item, ind) => {
        resData[ind]["checked"] = false;
      });
      setEmptyState((prev) => !prev);
    }
  }, [allCheck, resData]);

  return (
    <>
      <CustomLoader show={studentListLoading || studentListFetching} />

      <PrintCertificateDialog
        open={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        selectedStudents={getSelectedStudents()}
        schoolDetail={schoolDetail}
        selectedSession={selectedSession}
      />

      <Box
        sx={{
          width: "100%",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          p: 2,
        }}
      >
        <CertificateHeader
          selectedClasses={selectedClasses}
          setSelectedClasses={setSelectedClasses}
          studentListFetching={studentListFetching}
          onSearch={handleSearch}
          selectedStudentsCount={getSelectedStudents().length}
          onPrint={handlePrint}
          totalStudents={resData?.length}
        />

        <StudentTable
          resData={resData}
          allCheck={allCheck}
          setAllCheck={setAllCheck}
          setEmptyState={setEmptyState}
        />
      </Box>
    </>
  );
};

export default CertificatePrint;